// Admin Bookmarks — Steve's quick links.
//
// Source-of-truth reference: /var/www/kitchenos/admin-standalone.html
//   page section: ~line 1443 (#page-links — addBookmark / renderBookmarks)
//   data:         /var/www/kitchenos-api/data/admin_bookmarks.json
//
// Backend (in /var/www/kitchenos-api/dashboard.js, mounted at /api):
//   GET    /api/admin/bookmarks       — list
//   POST   /api/admin/bookmarks       — { name, url, category? }
//   PATCH  /api/admin/bookmarks/:id   — partial update
//   DELETE /api/admin/bookmarks/:id   — remove
//
// The server stores whatever fields we send; we use { name, url, category }.

import { ApiError } from "@tkos/api-client";

import { registerPage } from "../../shell/router";
import type { Page, PageContext, PageCleanup } from "../../shell/types";

import "./styles.css";

// ── Types ────────────────────────────────────────────────────────────────────

interface ServerBookmark {
  id: string;
  name?: string;
  title?: string;
  url: string;
  category?: string | null;
}

interface Bookmark {
  id: string;
  name: string;
  url: string;
  category: string;
}

// ── Helpers ──────────────────────────────────────────────────────────────────

function normalise(b: ServerBookmark): Bookmark {
  const url = String(b.url ?? "").trim();
  return {
    id: String(b.id ?? ""),
    name: String(b.name ?? b.title ?? url).trim() || url,
    url,
    category:
      typeof b.category === "string" && b.category.trim() !== ""
        ? b.category.trim()
        : "",
  };
}

function esc(s: string): string {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

function safeUrl(raw: string): string {
  const t = raw.trim();
  if (!t) return "";
  if (/^https?:\/\//i.test(t)) return t;
  if (t.startsWith("/") || t.startsWith("#")) return t;
  return `https://${t}`;
}

function hostOf(u: string): string {
  try {
    return new URL(u).hostname.replace(/^www\./, "");
  } catch {
    return u;
  }
}

function isAbortError(err: unknown): boolean {
  return err instanceof DOMException && err.name === "AbortError";
}

// ── Page ─────────────────────────────────────────────────────────────────────

const linksPage: Page = {
  name: "links",
  label: "Links",
  icon: "🔗",

  mount(container: HTMLElement, ctx: PageContext): PageCleanup {
    const ac = new AbortController();
    let bookmarks: Bookmark[] = [];
    let loading = true;
    let error: string | null = null;
    let categoryFilter = "all";

    const root = document.createElement("section");
    root.className = "alnk-page";
    root.innerHTML = `
      <header class="alnk-header">
        <h1>Admin Bookmarks</h1>
        <p>Quick links Steve uses. Open in a new tab; group by category.</p>
      </header>

      <div class="alnk-toolbar">
        <select class="adm-select alnk-cat" data-role="cat-filter" aria-label="Filter by category">
          <option value="all">All categories</option>
        </select>
        <span class="alnk-spacer"></span>
        <button class="adm-btn" type="button" data-role="add">+ Add link</button>
      </div>

      <div class="alnk-grid" data-role="list" aria-live="polite"></div>
    `;
    container.appendChild(root);

    const catEl = root.querySelector<HTMLSelectElement>("[data-role='cat-filter']")!;
    const listEl = root.querySelector<HTMLDivElement>("[data-role='list']")!;
    const addBtn = root.querySelector<HTMLButtonElement>("[data-role='add']")!;

    // Modal
    const dialog = document.createElement("dialog");
    dialog.className = "alnk-modal";
    dialog.innerHTML = `
      <form class="alnk-modal-form" data-role="form">
        <header class="alnk-modal-head">
          <h2>Add link</h2>
          <button type="button" class="alnk-modal-close" data-role="close" aria-label="Close">×</button>
        </header>
        <label>Name<input class="adm-input" name="name" required placeholder="e.g. Häfele Trade" /></label>
        <label>URL<input class="adm-input" name="url" required type="url" placeholder="https://…" /></label>
        <label>Category<input class="adm-input" name="category" placeholder="e.g. Suppliers" /></label>
        <footer class="alnk-modal-foot">
          <button type="button" class="adm-btn adm-btn-ghost" data-role="cancel">Cancel</button>
          <button type="submit" class="adm-btn">Save</button>
        </footer>
      </form>
    `;
    root.appendChild(dialog);

    const form = dialog.querySelector<HTMLFormElement>("[data-role='form']")!;
    const closeBtn = dialog.querySelector<HTMLButtonElement>("[data-role='close']")!;
    const cancelBtn = dialog.querySelector<HTMLButtonElement>("[data-role='cancel']")!;

    function categories(): string[] {
      const set = new Set<string>();
      for (const b of bookmarks) if (b.category) set.add(b.category);
      return [...set].sort();
    }

    function renderCategoryFilter(): void {
      const cats = categories();
      const selected = categoryFilter;
      catEl.innerHTML = `<option value="all">All categories</option>${cats
        .map((c) => `<option value="${esc(c)}">${esc(c)}</option>`)
        .join("")}`;
      // Restore selection if still valid
      if (cats.includes(selected)) catEl.value = selected;
      else {
        catEl.value = "all";
        categoryFilter = "all";
      }
    }

    function render(): void {
      if (loading) {
        listEl.innerHTML = `<div class="adm-loading">Loading…</div>`;
        return;
      }
      if (error) {
        listEl.innerHTML = `<div class="adm-error">${esc(error)}</div>`;
        return;
      }
      const visible = bookmarks.filter(
        (b) => categoryFilter === "all" || b.category === categoryFilter,
      );
      if (visible.length === 0) {
        listEl.innerHTML = `<div class="adm-empty">No bookmarks yet. Use "+ Add link" to add one.</div>`;
        return;
      }
      listEl.innerHTML = visible
        .map(
          (b) => `
        <div class="alnk-card" data-id="${esc(b.id)}">
          <a class="alnk-card-link" href="${esc(safeUrl(b.url))}" target="_blank" rel="noopener noreferrer">
            <div class="alnk-card-name">${esc(b.name)}</div>
            <div class="alnk-card-host">${esc(hostOf(safeUrl(b.url)))}</div>
            ${b.category ? `<div class="adm-pill alnk-cat-pill">${esc(b.category)}</div>` : ""}
          </a>
          <button class="alnk-del" type="button" data-role="delete" aria-label="Delete">×</button>
        </div>`,
        )
        .join("");
    }

    async function load(): Promise<void> {
      loading = true;
      error = null;
      render();
      try {
        const raw = await ctx.api<ServerBookmark[]>("/admin/bookmarks", { signal: ac.signal });
        bookmarks = (Array.isArray(raw) ? raw : []).map(normalise);
        loading = false;
        renderCategoryFilter();
        render();
      } catch (err) {
        if (isAbortError(err)) return;
        loading = false;
        error =
          err instanceof ApiError
            ? `Couldn't load bookmarks (${err.status}).`
            : "Couldn't load bookmarks.";
        render();
      }
    }

    async function addBookmark(name: string, url: string, category: string): Promise<void> {
      try {
        const created = await ctx.api<ServerBookmark>("/admin/bookmarks", {
          method: "POST",
          body: JSON.stringify({ name, url: safeUrl(url), category }),
          signal: ac.signal,
        });
        bookmarks = [normalise(created), ...bookmarks];
        renderCategoryFilter();
        render();
        ctx.showToast("Bookmark added", "success");
      } catch (err) {
        if (isAbortError(err)) return;
        ctx.showToast("Couldn't add bookmark", "error");
      }
    }

    async function deleteBookmark(id: string): Promise<void> {
      if (!window.confirm("Delete this bookmark?")) return;
      try {
        await ctx.api<{ ok: true }>(`/admin/bookmarks/${encodeURIComponent(id)}`, {
          method: "DELETE",
          signal: ac.signal,
        });
        bookmarks = bookmarks.filter((b) => b.id !== id);
        renderCategoryFilter();
        render();
      } catch (err) {
        if (isAbortError(err)) return;
        ctx.showToast("Couldn't delete bookmark", "error");
      }
    }

    function openModal(): void {
      form.reset();
      if (typeof dialog.showModal === "function") dialog.showModal();
      else dialog.setAttribute("open", "");
    }
    function closeModal(): void {
      if (typeof dialog.close === "function") dialog.close();
      else dialog.removeAttribute("open");
    }

    const onAddClick = (): void => openModal();
    const onCloseClick = (): void => closeModal();
    const onCancelClick = (): void => closeModal();

    const onSubmit = (e: Event): void => {
      e.preventDefault();
      const fd = new FormData(form);
      const name = String(fd.get("name") ?? "").trim();
      const url = String(fd.get("url") ?? "").trim();
      const category = String(fd.get("category") ?? "").trim();
      if (!name || !url) return;
      closeModal();
      void addBookmark(name, url, category);
    };

    const onCatChange = (): void => {
      categoryFilter = catEl.value;
      render();
    };

    const onListClick = (e: Event): void => {
      const t = e.target as HTMLElement;
      if (t.matches("[data-role='delete']")) {
        const card = t.closest<HTMLElement>(".alnk-card");
        const id = card?.dataset.id ?? "";
        if (id) void deleteBookmark(id);
      }
    };

    addBtn.addEventListener("click", onAddClick);
    closeBtn.addEventListener("click", onCloseClick);
    cancelBtn.addEventListener("click", onCancelClick);
    form.addEventListener("submit", onSubmit);
    catEl.addEventListener("change", onCatChange);
    listEl.addEventListener("click", onListClick);

    void load();

    return () => {
      ac.abort();
      addBtn.removeEventListener("click", onAddClick);
      closeBtn.removeEventListener("click", onCloseClick);
      cancelBtn.removeEventListener("click", onCancelClick);
      form.removeEventListener("submit", onSubmit);
      catEl.removeEventListener("change", onCatChange);
      listEl.removeEventListener("click", onListClick);
      if (dialog.open) closeModal();
      root.remove();
    };
  },
};

registerPage(linksPage);
export default linksPage;
